import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  ShoppingCartIcon,
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useBranch } from '../../context/BranchContext';
import { useTranslation } from 'react-i18next';
import CartPopup from '../cart/CartPopup';
import BranchSelector from './BranchSelector';
import LanguageSwitcher from '../language/LanguageSwitcher';
import SearchResults from '../search/SearchResults';
import { ProductItem, productService } from '../../services/product.service';
import UmeLogo from '../../assets/png-04.png';

// Define interfaces for menu items
type MenuItemHighlight = 'hot' | 'special' | 'coming-soon';

interface MenuItem {
  id: string;
  path: string;
  i18nKey: string;
  highlight?: MenuItemHighlight;
  emoji?: string;
  isHome?: boolean;
}

// Define menu items structure - Updated order according to new requirements
const menuItems: MenuItem[] = [
  {
    id: 'precutFruitBox',
    path: '/products/category/hop-trai-cay-cat-san',
    i18nKey: 'common.precutFruitBox' as const,
    highlight: 'hot',
    emoji: '🔥',
  },
  {
    id: 'mixedFruits',
    path: '/products/category/hoa-qua-dam',
    i18nKey: 'common.mixedFruits' as const,
  },
  {
    id: 'suggestedMixBox',
    path: '/products/category/hop-mix-cat-san-goi-y',
    i18nKey: 'common.suggestedMixBox' as const,
  },
  {
    id: 'greekYogurt',
    path: '/products/category/sua-chua-hy-lap',
    i18nKey: 'common.greekYogurt' as const,
  },
  {
    id: 'giftBox4Compartments',
    path: '/products/category/hop-qua-4-ngan',
    i18nKey: 'common.giftBox4Compartments' as const,
    highlight: 'hot',
    emoji: '🔥',
  },
  {
    id: 'giftBox6Compartments',
    path: '/products/category/hop-qua-6-ngan',
    i18nKey: 'common.giftBox6Compartments' as const,
  },
  {
    id: 'giftBox9Compartments',
    path: '/products/category/hop-qua-9-ngan',
    i18nKey: 'common.giftBox9Compartments' as const,
  },
  {
    id: 'fruitJuiceSmoothie',
    path: '/products/category/nuoc-ep-smoothie',
    i18nKey: 'common.fruitJuiceSmoothie' as const,
  },
  {
    id: 'wholeFruits',
    path: '/products/category/trai-cay-nguyen-qua',
    i18nKey: 'common.wholeFruits' as const,
  },
  {
    id: 'giftBox2Tiers',
    path: '/products/category/hop-qua-2-tang',
    i18nKey: 'common.giftBox2Tiers' as const,
  },
  {
    id: 'giftBoxPicnic',
    path: '/products/category/hop-qua-picnic',
    i18nKey: 'common.giftBoxPicnic' as const,
  },
  {
    id: 'customMixBox',
    path: '/custom-box/box-mix-hoa-qua-cat-san',
    i18nKey: 'common.customMixBox' as const,
    highlight: 'coming-soon',
  },
  {
    id: 'customGiftBox',
    path: '/custom-box/hop-qua-trai-cay-mix-tu-chon',
    i18nKey: 'common.customGiftBox' as const,
    highlight: 'coming-soon',
  },
  {
    id: 'blog',
    path: '/blog',
    i18nKey: 'common.blog' as const,
    emoji: '📝',
  },
];

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isHomeDropdownOpen, setIsHomeDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProductItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchResultsContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const mobileScrollContainerRef = useRef<HTMLDivElement>(null);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const cartPopupRef = useRef<HTMLDivElement>(null);
  const homeDropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const { user, isAuthenticated, logout } = useAuth();
  const { totalItems } = useCart();
  const { selectedBranch } = useBranch();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleCart = () => {
    setIsCartOpen(!isCartOpen);
    if (!isCartOpen) {
      setIsAccountOpen(false);
    }
  };

  const toggleAccount = () => {
    if (isAuthenticated) {
      setIsAccountOpen(!isAccountOpen);
      if (!isAccountOpen) {
        setIsCartOpen(false);
      }
    } else {
      navigate('/auth/login');
    }
  };

  const toggleHomeDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsHomeDropdownOpen(!isHomeDropdownOpen);
    if (!isHomeDropdownOpen) {
      setIsCartOpen(false);
      setIsAccountOpen(false);
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear any existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (query.trim().length > 0) {
      setIsSearching(true);

      // Set a new timeout
      const timeout = setTimeout(async () => {
        try {
          const response = await productService.getAllProducts({
            search: query,
            limit: 5,
            page: 1,
          });

          // Ensure the results are properly mapped to ProductItem[]
          if (
            response &&
            response.products &&
            Array.isArray(response.products)
          ) {
            setSearchResults(response.products);
          } else {
            console.error('Unexpected search response format:', response);
            setSearchResults([]);
          }
        } catch (error) {
          console.error('Error searching products:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 500);

      setSearchTimeout(timeout);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      setSearchTimeout(null);
    }
  };

  const handleSearchResultClick = () => {
    // Clear search results and query when clicking on a result
    setSearchResults([]);
    setSearchQuery('');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      clearSearch();
    }
  };

  const handleLogout = async () => {
    await logout();
    setIsAccountOpen(false);
    navigate('/');
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const scrollMenu = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      const currentScroll = scrollContainerRef.current.scrollLeft;

      scrollContainerRef.current.scrollTo({
        left:
          direction === 'left'
            ? currentScroll - scrollAmount
            : currentScroll + scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Helper function to render the menu item with appropriate styling
  const renderMenuItem = (item: MenuItem, isMobile: boolean = false) => {
    const baseDesktopClass = `h-30 w-30 flex items-center justify-center px-4 py-2 text-secondary hover:text-primary hover:bg-primary/5 rounded-lg transition-all duration-300 border-2`;
    const baseMobileClass = `flex items-center justify-center px-2 py-2 text-secondary hover:bg-primary/5 hover:text-primary transition-colors duration-300 border-2 rounded-lg w-[75px] h-[50px]`;

    const activeDesktopClass = `border-primary bg-primary/5 text-primary font-medium`;
    const activeMobileClass = `border-primary bg-primary/5 text-primary font-medium`;

    const inactiveDesktopClass = `border-gray-200 hover:border-primary/50`;
    const inactiveMobileClass = `border-gray-200 hover:border-primary/50`;

    let classes = isMobile ? baseMobileClass : baseDesktopClass;

    if (isActive(item.path)) {
      classes += isMobile ? ` ${activeMobileClass}` : ` ${activeDesktopClass}`;
    } else {
      classes += isMobile
        ? ` ${inactiveMobileClass}`
        : ` ${inactiveDesktopClass}`;
    }

    // Additional styling for special menu items
    if (item.highlight) {
      if (item.highlight === 'hot') {
        classes = isMobile
          ? `${baseMobileClass} border-orange-400/70 font-medium bg-gradient-to-r from-green-500/20 to-orange-500/20`
          : `${baseDesktopClass} border-orange-400/70 font-medium bg-gradient-to-r from-green-500/20 to-orange-500/20`;
      } else if (item.highlight === 'special') {
        classes = isMobile
          ? `${baseMobileClass} border-pink-400/70 font-medium bg-gradient-to-r from-pink-300/20 to-purple-400/30`
          : `${baseDesktopClass} border-pink-400/70 font-medium bg-gradient-to-r from-pink-300/20 to-purple-400/30`;
      } else if (item.highlight === 'coming-soon') {
        classes = isMobile
          ? `${baseMobileClass} border-blue-400/70 font-medium bg-gradient-to-r from-blue-300/20 to-indigo-400/30`
          : `${baseDesktopClass} border-blue-400/70 font-medium bg-gradient-to-r from-blue-300/20 to-indigo-400/30`;
      }
    }

    // Function to get appropriate font size for mobile text
    const getMobileTextClass = (text: string): string => {
      const length = text.length;
      if (length <= 10) return 'text-xs';
      if (length <= 15) return 'text-[10px]';
      if (length <= 20) return 'text-[9px]';
      return 'text-[8px]';
    };

    let content;
    if (item.highlight) {
      const translatedText = t(item.i18nKey as any);
      const textSizeClass = isMobile ? getMobileTextClass(translatedText) : '';

      content = (
        <div className='flex items-center justify-center relative w-full h-full'>
          <span
            className={`${
              isMobile
                ? `${textSizeClass} text-center line-clamp-2 w-full leading-tight px-1`
                : 'truncate'
            }`}
          >
            {item.emoji ? `${item.emoji} ` : ''}
            {translatedText}
          </span>
          {item.highlight === 'hot' && (
            <span
              className={`absolute -top-2 -right-1 bg-gradient-to-r from-primary to-orange-500 text-white text-[9px] font-bold px-1 py-0 rounded-full animate-pulse transform rotate-25 shadow-sm ${
                isMobile ? 'hidden' : ''
              }`}
            >
              {t('product.hot')}
            </span>
          )}
          {item.highlight === 'special' && (
            <span
              className={`absolute -top-2 -right-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-[9px] font-bold px-1 py-0 rounded-full animate-pulse transform rotate-25 shadow-sm ${
                isMobile ? 'hidden' : ''
              }`}
            >
              ĐẶC BIỆT
            </span>
          )}
          {item.highlight === 'coming-soon' && (
            <span
              className={`absolute -top-2 -right-1 bg-blue-500 text-white text-[9px] font-bold px-1 py-0 rounded-full transform rotate-25 shadow-sm ${
                isMobile ? 'hidden' : ''
              }`}
            >
              SẮP RA MẮT
            </span>
          )}
        </div>
      );
    } else {
      const translatedText = t(item.i18nKey as any);
      const textSizeClass = isMobile ? getMobileTextClass(translatedText) : '';

      content = (
        <div className='flex items-center justify-center w-full h-full'>
          <span
            className={`${
              isMobile
                ? `${textSizeClass} text-center line-clamp-2 w-full leading-tight px-1`
                : 'truncate'
            }`}
          >
            {translatedText}
          </span>
        </div>
      );
    }



    return (
      <Link
        key={item.id}
        to={item.path}
        className={classes}
        title={t(item.i18nKey as any)}
      >
        {content}
      </Link>
    );
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Handle home dropdown
      if (
        homeDropdownRef.current &&
        !homeDropdownRef.current.contains(event.target as Node) &&
        isHomeDropdownOpen
      ) {
        setIsHomeDropdownOpen(false);
      }

      // Handle account menu
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target as Node) &&
        isAccountOpen
      ) {
        setIsAccountOpen(false);
      }

      // Handle cart popup
      if (
        cartPopupRef.current &&
        !cartPopupRef.current.contains(event.target as Node) &&
        isCartOpen &&
        !(event.target as HTMLElement).closest('.cart-button')
      ) {
        setIsCartOpen(false);
      }

      // Handle search results - only clear if clicking outside search container AND not on search results
      if (
        searchResultsContainerRef.current &&
        !searchResultsContainerRef.current.contains(event.target as Node) &&
        !searchInputRef.current?.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('[data-search-result]') &&
        searchResults.length > 0
      ) {
        setSearchResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isHomeDropdownOpen, isAccountOpen, isCartOpen, searchResults.length]);

  // Debug useEffect for home dropdown
  useEffect(() => {
    // console.log('Home dropdown state changed to:', isHomeDropdownOpen);
  }, [isHomeDropdownOpen]);

  // Add wheel event handler for mobile scroll container too
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (scrollContainerRef.current && e.deltaY !== 0) {
        e.preventDefault();
        scrollContainerRef.current.scrollLeft += e.deltaY;
      }
    };

    const handleMobileWheel = (e: WheelEvent) => {
      if (mobileScrollContainerRef.current && e.deltaY !== 0) {
        e.preventDefault();
        mobileScrollContainerRef.current.scrollLeft += e.deltaY;
      }
    };

    const scrollContainer = scrollContainerRef.current;
    const mobileScrollContainer = mobileScrollContainerRef.current;

    if (scrollContainer) {
      scrollContainer.addEventListener('wheel', handleWheel, {
        passive: false,
      });
    }

    if (mobileScrollContainer) {
      mobileScrollContainer.addEventListener('wheel', handleMobileWheel, {
        passive: false,
      });
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('wheel', handleWheel);
      }
      if (mobileScrollContainer) {
        mobileScrollContainer.removeEventListener('wheel', handleMobileWheel);
      }
    };
  }, []);

  // Clean up the timeout when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Clear search when location changes
  useEffect(() => {
    if (searchResults.length > 0) {
      setSearchResults([]);
    }
  }, [location.pathname]);

  return (
    <header className='bg-accent shadow-md sticky top-0 z-40'>
      <div className='container mx-auto'>
        <div className='flex items-center justify-between'>
          {/* Left Block: Logo + Menu */}
          <div className='flex items-center space-x-6'>
            {/* Logo */}
            <Link
              to='/'
              className='flex items-center text-3xl font-bold text-primary hover:text-primary/80 transition-colors duration-300'
            >
              <img src={UmeLogo} alt='Ume Fruits Logo' className='h-20' />
            </Link>

            {/* Menu Dropdown */}
            <div className='relative' ref={homeDropdownRef}>
              <div className='relative overflow-hidden rounded-lg'>
                <button
                  onClick={toggleHomeDropdown}
                  className={`flex items-center text-secondary hover:text-primary transition-all duration-300 px-4 py-2 rounded-lg font-bold relative group ${
                    isHomeDropdownOpen
                      ? 'text-primary bg-primary/10 shadow-lg transform scale-105'
                      : 'hover:bg-primary/5 hover:shadow-md hover:transform hover:scale-105'
                  }`}
                  title='Menu'
                >
                  <span className={`text-sm font-bold tracking-wider relative z-10 ${
                    isHomeDropdownOpen 
                      ? 'text-primary' 
                      : 'group-hover:text-primary'
                  }`}>
                    MENU
                  </span>
                  {/* Animated underline */}
                  <div className={`absolute bottom-0 left-0 h-0.5 bg-primary transition-all duration-300 ${
                    isHomeDropdownOpen 
                      ? 'w-full' 
                      : 'w-0 group-hover:w-full'
                  }`}></div>
                  {/* Glow effect */}
                  <div className={`absolute inset-0 rounded-lg transition-all duration-300 ${
                    isHomeDropdownOpen 
                      ? 'bg-primary/5 shadow-lg' 
                      : 'group-hover:bg-primary/5 group-hover:shadow-lg'
                  }`}></div>
                  {/* Running light effect - 2 streams */}
                  <div className={`absolute inset-0 ${
                    isHomeDropdownOpen ? 'opacity-0' : 'opacity-100'
                  }`}>
                  {/* First light stream */}
                  <div className={`absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent transform ${
                    isHomeDropdownOpen 
                      ? 'translate-x-full' 
                      : 'animate-[running-light_3s_ease-in-out_infinite]'
                  }`}></div>
                  <div className={`absolute top-0 right-0 w-0.5 h-full bg-gradient-to-b from-transparent via-primary to-transparent transform ${
                    isHomeDropdownOpen 
                      ? 'translate-y-full' 
                      : 'animate-[running-light-vertical_3s_ease-in-out_infinite_0.75s]'
                  }`}></div>
                  <div className={`absolute bottom-0 right-0 w-full h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent transform ${
                    isHomeDropdownOpen 
                      ? '-translate-x-full' 
                      : 'animate-[running-light-reverse_3s_ease-in-out_infinite_1.5s]'
                  }`}></div>
                  <div className={`absolute bottom-0 left-0 w-0.5 h-full bg-gradient-to-b from-transparent via-primary to-transparent transform ${
                    isHomeDropdownOpen 
                      ? '-translate-y-full' 
                      : 'animate-[running-light-vertical-reverse_3s_ease-in-out_infinite_2.25s]'
                  }`}></div>
                  
                  {/* Second light stream */}
                  <div className={`absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent transform ${
                    isHomeDropdownOpen 
                      ? 'translate-x-full' 
                      : 'animate-[running-light_3s_ease-in-out_infinite_1.5s]'
                  }`}></div>
                  <div className={`absolute top-0 right-0 w-0.5 h-full bg-gradient-to-b from-transparent via-primary to-transparent transform ${
                    isHomeDropdownOpen 
                      ? 'translate-y-full' 
                      : 'animate-[running-light-vertical_3s_ease-in-out_infinite_2.25s]'
                  }`}></div>
                  <div className={`absolute bottom-0 right-0 w-full h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent transform ${
                    isHomeDropdownOpen 
                      ? '-translate-x-full' 
                      : 'animate-[running-light-reverse_3s_ease-in-out_infinite_3s]'
                  }`}></div>
                  <div className={`absolute bottom-0 left-0 w-0.5 h-full bg-gradient-to-b from-transparent via-primary to-transparent transform ${
                    isHomeDropdownOpen 
                      ? '-translate-y-full' 
                      : 'animate-[running-light-vertical-reverse_3s_ease-in-out_infinite_3.75s]'
                  }`}></div>
                </div>
              </button>
              </div>

              {/* Menu Dropdown */}
              {isHomeDropdownOpen && (
                <div className='absolute top-full left-0 w-56 md:w-64 mt-2 py-2 bg-white rounded-lg shadow-xl z-50 border border-gray-100 max-h-80 md:max-h-96 overflow-y-auto'>
                  <Link
                    to='/'
                    className={`block px-3 md:px-4 py-2 border-b border-gray-100 rounded-t-lg transition-colors duration-300 ${
                      location.pathname === '/' 
                        ? 'bg-primary/10 text-primary font-medium' 
                        : 'text-secondary hover:bg-primary/5 hover:text-primary'
                    }`}
                    onClick={() => setIsHomeDropdownOpen(false)}
                  >
                    <p className='text-xs md:text-sm flex items-center'>
                      <HomeIcon className='h-3 w-3 md:h-4 md:w-4 mr-2' />
                      {t('common.home')}
                    </p>
                  </Link>
                  
                  {/* All categories */}
                  {menuItems.map((menuItem) => {
                    const isActive = location.pathname === menuItem.path;
                    return (
                      <Link
                        key={menuItem.id}
                        to={menuItem.path}
                        className={`block px-3 md:px-4 py-2 text-xs md:text-sm transition-colors duration-300 flex items-center justify-between ${
                          isActive 
                            ? 'bg-primary/10 text-primary font-medium border-l-4 border-primary' 
                            : 'text-secondary hover:bg-primary/5 hover:text-primary'
                        }`}
                        onClick={() => setIsHomeDropdownOpen(false)}
                      >
                      <span className='flex items-center'>
                        {menuItem.emoji && <span className='mr-2'>{menuItem.emoji}</span>}
                        {t(menuItem.i18nKey as any)}
                      </span>
                      {menuItem.highlight && (
                        <span className={`text-xs font-bold px-1 md:px-2 py-0.5 md:py-1 rounded-full ${
                          menuItem.highlight === 'hot' 
                            ? 'bg-gradient-to-r from-primary to-orange-500 text-white' 
                            : menuItem.highlight === 'special'
                            ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white'
                            : 'bg-blue-500 text-white'
                        }`}>
                          {menuItem.highlight === 'hot' ? t('product.hot') : 
                           menuItem.highlight === 'special' ? t('common.special') : 
                           t('common.comingSoon')}
                        </span>
                      )}
                    </Link>
                  );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Center Block: Search and Branch Selector */}
          <div className='hidden md:flex flex-1 mx-6 items-center space-x-3'>
            {/* Search */}
            <div className='flex-1 relative' ref={searchResultsContainerRef}>
              <form onSubmit={handleSearch}>
                <div className='relative group'>
                  <input
                    type='text'
                    placeholder={t('common.searchPlaceholder')}
                    className='w-full py-2 pl-4 pr-10 border-2 border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 hover:border-primary/50 bg-white text-secondary'
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    ref={searchInputRef}
                  />
                  {isSearching ? (
                    <div className='absolute right-0 top-0 h-full px-3 text-gray-500 flex items-center'>
                      <svg
                        className='animate-spin h-5 w-5 text-primary'
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                      >
                        <circle
                          className='opacity-25'
                          cx='12'
                          cy='12'
                          r='10'
                          stroke='currentColor'
                          strokeWidth='4'
                        ></circle>
                        <path
                          className='opacity-75'
                          fill='currentColor'
                          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                        ></path>
                      </svg>
                    </div>
                  ) : (
                    <button
                      type='submit'
                      className='absolute right-0 top-0 h-full px-3 text-gray-500 hover:text-primary transition-colors duration-300'
                    >
                      <MagnifyingGlassIcon className='h-5 w-5' />
                    </button>
                  )}
                </div>
              </form>

              {/* Only show SearchResults if not on search page */}
              {!location.pathname.startsWith('/search') && (
                <SearchResults
                  results={searchResults}
                  loading={isSearching}
                  onResultClick={handleSearchResultClick}
                  searchQuery={searchQuery}
                />
              )}
            </div>

            {/* Branch selector */}
            <div className='w-[240px] flex-shrink-0'>
              <BranchSelector className='w-full' />
            </div>
          </div>

          {/* Right Block: Language + Cart + Account + Mobile Menu */}
          <div className='flex items-center space-x-6'>
            {/* Language switcher */}
            <LanguageSwitcher />

            {/* Cart */}
            <div className='relative'>
              <button
                onClick={toggleCart}
                className='relative text-secondary hover:text-primary transition-colors duration-300 group cart-button'
                title={t('common.cart')}
              >
                <ShoppingCartIcon className='h-6 w-6 group-hover:scale-110 transition-transform duration-300' />
                {totalItems > 0 && (
                  <span className='absolute -top-2 -right-2 bg-primary text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center cart-badge'>
                    {totalItems}
                  </span>
                )}
              </button>

              {/* Cart Popup Dropdown */}
              <div className='cart-popup-container' ref={cartPopupRef}>
                {isCartOpen && (
                  <CartPopup
                    isOpen={isCartOpen}
                    onClose={() => setIsCartOpen(false)}
                  />
                )}
              </div>
            </div>

            {/* Account */}
            <div className='relative' ref={accountMenuRef}>
              <button
                onClick={toggleAccount}
                className='flex items-center space-x-1 text-secondary hover:text-primary transition-colors duration-300'
                title={
                  isAuthenticated ? t('common.account') : t('common.login')
                }
              >
                <UserIcon className='h-6 w-6 group-hover:scale-110 transition-transform duration-300' />
                <span className='hidden md:inline text-sm'>
                  {isAuthenticated
                    ? user?.name?.split(' ')[0] || t('common.account')
                    : t('common.login')}
                </span>
              </button>

              {/* Account dropdown - now using toggle approach */}
              {isAuthenticated && isAccountOpen && (
                <div className='absolute right-0 w-48 mt-2 py-2 bg-white rounded-lg shadow-xl z-10 animate-fadeIn'>
                  <div className='px-4 py-2 border-b border-gray-100 bg-accent rounded-t-lg'>
                    <p className='text-sm font-medium text-secondary truncate'>
                      {user?.name}
                    </p>
                    <p className='text-xs text-secondary truncate'>
                      {user?.email}
                    </p>
                  </div>
                  <Link
                    to='/account'
                    className='block px-4 py-2 text-sm text-secondary hover:bg-primary/5 hover:text-primary transition-colors duration-300'
                    onClick={() => setIsAccountOpen(false)}
                  >
                    {t('footer.myAccount')}
                  </Link>
                  <Link
                    to='/order-history'
                    className='block px-4 py-2 text-sm text-secondary hover:bg-primary/5 hover:text-primary transition-colors duration-300'
                    onClick={() => setIsAccountOpen(false)}
                  >
                    {t('cart.myOrders')}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className='block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-300'
                  >
                    {t('common.logout')}
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className='flex md:hidden'>
              <button
                onClick={toggleMenu}
                className='text-secondary hover:text-primary transition-colors duration-300'
              >
                {isMenuOpen ? (
                  <XMarkIcon className='h-6 w-6' />
                ) : (
                  <Bars3Icon className='h-6 w-6' />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Navigation - Desktop */}
        <div className='hidden md:block relative px-10'>
          <button
            onClick={() => scrollMenu('left')}
            className='absolute top-1/2 -translate-y-1/2 z-20 bg-white/90 rounded-full p-1.5 shadow-md hover:bg-white transition-all border border-primary/20 text-primary hover:text-primary/80 hover:border-primary/50'
            aria-label='Scroll left'
            style={{ left: '0px' }}
          >
            <ChevronLeftIcon className='h-6 w-6' />
          </button>

          <div
            ref={scrollContainerRef}
            className='overflow-x-auto scrollbar-hide w-full'
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <nav className='flex space-x-2 min-w-max pb-2'>
              {menuItems.map((item) => renderMenuItem(item))}
            </nav>
          </div>

          <button
            onClick={() => scrollMenu('right')}
            className='absolute top-1/2 -translate-y-1/2 z-20 bg-white/90 rounded-full p-1.5 shadow-md hover:bg-white transition-all border border-primary/20 text-primary hover:text-primary/80 hover:border-primary/50'
            aria-label='Scroll right'
            style={{ right: '0px' }}
          >
            <ChevronRightIcon className='h-6 w-6' />
          </button>
          <div className='absolute left-0 top-0 bg-gradient-to-r from-accent via-accent to-transparent w-16 h-full pointer-events-none'></div>
          <div className='absolute right-0 top-0 bg-gradient-to-l from-accent via-accent to-transparent w-16 h-full pointer-events-none'></div>
        </div>

        {/* Mobile navigation - Full width search (Branch selector moved to menu dropdown) */}
        <div className='md:hidden px-4 py-2 bg-white border-t border-gray-200'>
          <div className='relative' ref={searchResultsContainerRef}>
            <form onSubmit={handleSearch}>
              <div className='relative group'>
                <input
                  type='text'
                  placeholder={t('common.searchPlaceholder')}
                  className='w-full py-2 pl-4 pr-10 border-2 border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 hover:border-primary/50 bg-white text-secondary text-sm'
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                />
                {isSearching ? (
                  <div className='absolute right-0 top-0 h-full px-3 text-gray-500 flex items-center'>
                    <svg
                      className='animate-spin h-5 w-5 text-primary'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                    >
                      <circle
                        className='opacity-25'
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='4'
                      ></circle>
                      <path
                        className='opacity-75'
                        fill='currentColor'
                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                      ></path>
                    </svg>
                  </div>
                ) : (
                  <button
                    type='submit'
                    className='absolute right-0 top-0 h-full px-3 text-gray-500 hover:text-primary transition-colors duration-300'
                  >
                    <MagnifyingGlassIcon className='h-5 w-5' />
                  </button>
                )}
              </div>
            </form>

            {/* Only show SearchResults if not on search page */}
            {!location.pathname.startsWith('/search') && (
              <SearchResults
                results={searchResults}
                loading={isSearching}
                onResultClick={handleSearchResultClick}
                searchQuery={searchQuery}
              />
            )}
          </div>
        </div>

        {/* Mobile navigation - Horizontal scrolling menu (HIDDEN - replaced by Menu dropdown) */}
        {/* <div className='md:hidden relative px-5 py-2'>
          <div
            ref={mobileScrollContainerRef}
            className='overflow-x-auto scrollbar-hide w-full'
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <nav className='flex space-x-2 min-w-max pl-3'>
              {menuItems.map((item) => (
                <div key={item.id} className='flex-shrink-0'>
                  {renderMenuItem(item, true)}
                </div>
              ))}
            </nav>
          </div>
          <div className='absolute left-0 top-0 bg-gradient-to-r from-accent via-accent to-transparent w-12 h-full pointer-events-none'></div>
          <div className='absolute right-0 top-0 bg-gradient-to-l from-accent via-accent to-transparent w-12 h-full pointer-events-none'></div>
        </div> */}

        {/* Mobile menu - Branch Selector */}
        {isMenuOpen && (
          <div className='md:hidden mt-2 bg-white border-t border-gray-200 rounded-lg shadow-lg'>
            <div className='flex flex-col py-2'>
              {/* Branch Selector in Mobile Menu */}
              <div className='px-4 py-3 border-b border-gray-100'>
                <BranchSelector className='w-full' />
              </div>
              
              {/* Additional mobile menu options */}
              <div className='px-4 py-3 text-center text-gray-500 text-sm'>
                Sử dụng Menu dropdown để truy cập các danh mục
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
